import axios from 'axios';
import { notifySessionExpired } from './sessionExpiry';

/**
 * Installs global 401 handling for both axios and `fetch`.
 *
 * Before this existed the client had no HTTP error interception at all: `AuthContext` checked
 * authentication once at boot and `ProtectedRoute` never re-evaluated, so a 401 on any data
 * call surfaced as an infinite spinner, an empty table, or an unhandled rejection depending on
 * the component. A rejected promise inside a `useEffect` never reaches an error boundary.
 *
 * Both handlers are strictly observational: they inspect the status and then hand the original
 * result or rejection straight back, so existing per-call error handling is unaffected.
 *
 * Call exactly once, as early as possible in app startup.
 */

/**
 * Requests to the auth routes are excluded.
 *
 * `/auth/check` returning 401 is the *normal* logged-out path taken on every cold load, and
 * `/auth/logout` and `/auth/refresh-session` legitimately 401 against a dead session. Treating
 * any of them as an expiry event would fire a spurious notification on every anonymous page
 * load — and, on the login page, on a loop.
 */
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return /\/(api\/)?auth\//.test(url);
}

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  // Request
  return input.url;
}

let installed = false;

export function installHttpAuthHandlers(): void {
  if (installed) return;
  installed = true;

  // --- axios -------------------------------------------------------------------------
  // Every service in the app calls the default axios instance directly (there is no
  // axios.create anywhere), so a single interceptor here covers all of them.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url: string | undefined = error?.config?.url;

      if (status === 401 && !isAuthEndpoint(url)) {
        notifySessionExpired();
      }

      // Preserve existing behaviour for every caller.
      return Promise.reject(error);
    }
  );

  // --- fetch -------------------------------------------------------------------------
  // Bugsnag's performance SDK also wraps fetch (autoInstrumentNetworkRequests). Capturing
  // whatever is currently installed and delegating to it keeps both wrappers working
  // regardless of installation order.
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const previousFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const response = await previousFetch(input, init);

      if (response.status === 401 && !isAuthEndpoint(resolveFetchUrl(input))) {
        notifySessionExpired();
      }

      return response;
    };
  }
}

/** Test seam. Does not un-wrap anything; only allows a re-install in a fresh test context. */
export function resetHttpAuthHandlersForTests(): void {
  installed = false;
}
